/**
 * Panel resolution logic for MegaMenu CMS data → component props transform.
 *
 * Handles:
 * - panelRef resolution from the panels map
 * - panel vs panelRef conflict (panelRef wins)
 * - Layout validation with fallback to valid subset
 *
 * This module is consumed by resolveMegaMenuProps (the shared transform layer
 * described in Decision 6) and will be imported by PolyR, the hydration bundle,
 * and CMS preview.
 *
 * @see https://pippin.sara.amazon.dev/architect/mega_menu_cms_authoring?artifact=07panellayouttemplates
 */

import { validatePanelLayout } from './PanelLayout';
import type { ColumnType, ColumnVariant } from './PanelLayout';

// ── CMS Data Types ───────────────────────────────────────────────────────

/** A column as stored in the CMS entity */
export interface CmsColumn {
  type: ColumnType;
  variant?: ColumnVariant;
  [key: string]: unknown;
}

/** A panel as stored in the CMS entity */
export interface CmsPanel {
  templateId?: string;
  columns: CmsColumn[];
  footer?: { links: unknown[] };
}

/** A nav group as stored in the CMS entity */
export interface CmsNavGroup {
  label: string;
  href?: string;
  panel?: CmsPanel;
  panelRef?: string;
  [key: string]: unknown;
}

/** Top-level CMS mega menu payload */
export interface CmsMegaMenuPayload {
  navGroups: CmsNavGroup[];
  panels?: Record<string, CmsPanel>;
  [key: string]: unknown;
}

// ── Resolution ───────────────────────────────────────────────────────────

/**
 * Resolve a nav group's panel, handling panelRef lookup and conflict resolution.
 *
 * Priority: panelRef > inline panel.
 * If panelRef points to a missing key, falls back to inline panel with a warning.
 */
export function resolvePanel(
  navGroup: CmsNavGroup,
  panelsMap: Record<string, CmsPanel> | undefined,
): CmsPanel | undefined {
  const { panel, panelRef } = navGroup;

  if (panelRef) {
    if (!panelsMap) {
      console.warn(
        `[MegaMenu] panelRef "${panelRef}" on nav group "${navGroup.label}" but no panels map provided. Falling back to inline panel.`,
      );
    } else {
      const resolved = panelsMap[panelRef];
      if (resolved) {
        if (panel) {
          console.warn(
            `[MegaMenu] Nav group "${navGroup.label}" has both panel and panelRef. Using panelRef "${panelRef}".`,
          );
        }
        return resolved;
      }
      // panelRef points to missing key — fall back to inline
      console.warn(
        `[MegaMenu] panelRef "${panelRef}" not found in panels map for nav group "${navGroup.label}". Falling back to inline panel.`,
      );
    }
  }

  return panel;
}

/**
 * Validate a resolved panel's layout. If invalid, filter to the valid subset of columns.
 *
 * Returns the panel with only valid columns, or undefined if no valid columns remain.
 */
export function validateAndFilterPanel(panel: CmsPanel): CmsPanel | undefined {
  const result = validatePanelLayout(panel.columns);
  if (result.valid) return panel;

  console.warn(`[MegaMenu] Invalid panel layout: ${result.reason}. Rendering valid subset.`);

  // Greedy left-to-right filter: try adding each column in order; skip any that would make
  // the set invalid. Column order in CMS data determines which columns survive — e.g.
  // [featured, featured, single] → [featured, single] (second featured silently dropped).
  const validColumns: CmsColumn[] = [];
  for (const col of panel.columns) {
    const candidate = [...validColumns, col];
    if (validatePanelLayout(candidate).valid) {
      validColumns.push(col);
    }
  }

  if (validColumns.length === 0) {
    console.warn('[MegaMenu] No valid columns remain after filtering — panel will not render.');
    return undefined;
  }

  return { ...panel, columns: validColumns };
}

/**
 * Resolve all panels in a CMS mega menu payload.
 *
 * For each nav group:
 * 1. Resolve panelRef → inline panel (with conflict handling)
 * 2. Validate layout, filter to valid subset if needed
 */
export function resolveAllPanels(payload: CmsMegaMenuPayload): CmsMegaMenuPayload {
  const resolvedNavGroups = payload.navGroups.map(navGroup => {
    const resolved = resolvePanel(navGroup, payload.panels);
    if (!resolved) return { ...navGroup, panel: undefined, panelRef: undefined };

    const validated = validateAndFilterPanel(resolved);
    if (!validated) return { ...navGroup, panel: undefined, panelRef: undefined };
    return { ...navGroup, panel: validated, panelRef: undefined };
  });

  return { ...payload, navGroups: resolvedNavGroups };
}
